import { NoResultsRow } from '@folio/components/module-elements/PolicyTable/NoResultsRow';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { PaginationComponent } from '../../components/ui/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { DateInput } from '../../components/forms/date-input';
import { Input } from '../../components/forms/input';
import { MultiSelect } from '../../components/forms/multi-select/MultiSelect';
import { groupTerminationReasonOptions } from './groupTerminationReasonOptions';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/forms/select';
import { formatDate } from '../../lib/date-formatting';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  keyValueFieldsToObject,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerGetAccountingSummaryForBrokerageFirmQueryOptions,
  ViewSpecificControllerGetAccountingSummaryForBrokerageFirmQueryParamsStatusesEnumKey,
} from 'korint-sdk';
import { useFeatureFlag, useKorintModuleConfig, useKorintProductConfig } from 'korint-sdk/react';
import _ from 'lodash';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  EyeIcon,
  ListFilter,
  LucideInfo,
  SearchIcon,
  XIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePolicyStatuses } from '../../hooks/usePolicyStatuses';
import { PolicyStatus, keepSignedStatuses } from './components/PolicyStatus';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import('dayjs/locale/fr');
import('dayjs/locale/en');

dayjs.extend(relativeTime);

const PAGE_SIZE = 10;

const COMPLETE_DAY = /^[1-9]\d{3}-\d{2}-\d{2}$/;

// Older URLs stored the bound as an ISO timestamp rather than the day typed
const toInvoiceDateDay = (date: string | undefined) =>
  date && date.length > 10 ? dayjs(date).format('YYYY-MM-DD') : date;

// A date input emits partial years (0002-…) while typing, and dayjs reads years < 100 as 19xx
const toInvoiceDateBound = (date: string | undefined, bound: 'start' | 'end') => {
  const day = toInvoiceDateDay(date);
  if (!day || !COMPLETE_DAY.test(day)) return undefined;
  const parsed = dayjs(day);
  return (bound === 'start' ? parsed.startOf('day') : parsed.endOf('day')).toISOString();
};

export function AccountingDashboardModule() {
  const { t, i18n } = useTranslation();

  const accountingDashboardConfig = useKorintModuleConfig('accountingDashboard');
  const { productsConfig } = useKorintProductConfig();
  const productOptions = useMemo(
    () =>
      _.map(productsConfig, (_config, productId) => ({
        value: productId,
        label: t(`products.${productId}.label`),
      })),
    [productsConfig, t],
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filter values from URL params
  const productIds = useMemo(() => {
    const urlProductIds = searchParams.getAll('productIds');
    if (urlProductIds.length === 0 || productOptions.length === 0) return [];
    return urlProductIds
      .map(id => productOptions.find(opt => opt.value === id))
      .filter((opt): opt is { value: string; label: string } => opt !== undefined);
  }, [searchParams, productOptions]);

  const dateFilter = useMemo(
    () => ({
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
    }),
    [searchParams],
  );

  const param = searchParams.get('hasFormalNotice');
  const hasFormalNotice = param === 'true' ? true : param === 'false' ? false : undefined;

  // The dashboard spans a brokerage firm, so only a single-product app can scope the flag
  const automaticRetryEnabled = useFeatureFlag(
    'feat_automatic_unpaid_retry',
    productOptions.length === 1 ? productOptions[0].value : undefined,
  );

  const retryParam = searchParams.get('hasAutomaticRetry');
  const hasAutomaticRetry = !automaticRetryEnabled
    ? undefined
    : retryParam === 'true'
      ? true
      : retryParam === 'false'
        ? false
        : undefined;

  const unpaidDurationDays = useMemo(() => {
    const value = searchParams.get('unpaidDurationDays');
    return value ? Number.parseInt(value, 10) : undefined;
  }, [searchParams]);

  const hasActiveFilters =
    searchParams.getAll('statusIds').length > 0 ||
    searchParams.getAll('productIds').length > 0 ||
    searchParams.getAll('terminationReasonIds').length > 0 ||
    dateFilter.startDate !== undefined ||
    dateFilter.endDate !== undefined ||
    hasFormalNotice !== undefined ||
    hasAutomaticRetry !== undefined ||
    unpaidDurationDays !== undefined;

  const [showFilters, setShowFilters] = useState(false);

  const [filterFields, setFilterFields] = useState<string[]>(() => {
    const initialFields: string[] = [];
    const hasStatus = searchParams.getAll('statusIds').length > 0;
    const hasInvoiceDate =
      (searchParams.get('startDate') ?? '') !== '' || (searchParams.get('endDate') ?? '') !== '';
    const hasProducts = searchParams.getAll('productIds').length > 0;
    const hasTerminationReasons = searchParams.getAll('terminationReasonIds').length > 0;
    const hasFormalNoticeFilter = hasFormalNotice !== undefined;
    const hasAutomaticRetryFilter = hasAutomaticRetry !== undefined;
    const hasUnpaidDurationFilter = unpaidDurationDays !== undefined;

    if (hasStatus) {
      initialFields.push('status');
    }
    if (hasInvoiceDate) {
      initialFields.push('invoiceDate');
    }
    if (hasProducts && productOptions.length > 1) {
      initialFields.push('product');
    }
    if (hasTerminationReasons) {
      initialFields.push('terminationReason');
    }
    if (hasFormalNoticeFilter) {
      initialFields.push('formalNotice');
    }
    if (hasAutomaticRetryFilter) {
      initialFields.push('automaticRetry');
    }
    if (hasUnpaidDurationFilter) {
      initialFields.push('unpaidDuration');
    }

    return Array.from(new Set(initialFields));
  });

  // Sync filterFields with URL params - URL determines base filters, but allow manually added ones
  useEffect(() => {
    const urlFields: string[] = [];
    const hasStatus = searchParams.getAll('statusIds').length > 0;
    const hasInvoiceDate =
      (searchParams.get('startDate') ?? '') !== '' || (searchParams.get('endDate') ?? '') !== '';
    const hasProducts = searchParams.getAll('productIds').length > 0;
    const hasTerminationReasons = searchParams.getAll('terminationReasonIds').length > 0;
    const hasFormalNoticeFilter = hasFormalNotice !== undefined;
    const hasAutomaticRetryFilter = hasAutomaticRetry !== undefined;
    const hasUnpaidDurationFilter = unpaidDurationDays !== undefined;

    if (hasStatus) {
      urlFields.push('status');
    }
    if (hasInvoiceDate) {
      urlFields.push('invoiceDate');
    }
    if (hasProducts && productOptions.length > 1) {
      urlFields.push('product');
    }
    if (hasTerminationReasons) {
      urlFields.push('terminationReason');
    }
    if (hasFormalNoticeFilter) {
      urlFields.push('formalNotice');
    }
    if (hasAutomaticRetryFilter) {
      urlFields.push('automaticRetry');
    }
    if (hasUnpaidDurationFilter) {
      urlFields.push('unpaidDuration');
    }

    // Merge: preserve order of existing filters, then append new URL-based filters, then manually added ones
    setFilterFields(prev => {
      const urlFieldsSet = new Set(urlFields);
      const prevFieldsSet = new Set(prev);

      // Preserve order: keep existing filters in their current order if they're in URL
      const orderedUrlFields = prev.filter(field => urlFieldsSet.has(field));
      const newUrlFields = urlFields.filter(field => !prevFieldsSet.has(field));
      const manuallyAdded = prev.filter(field => !urlFieldsSet.has(field));

      // Combine: existing URL filters (preserved order) + new URL filters + manually added
      return [...orderedUrlFields, ...newUrlFields, ...manuallyAdded];
    });
  }, [searchParams, productOptions.length, hasFormalNotice, hasAutomaticRetry, unpaidDurationDays]);

  // Auto-show filters if any are active
  useEffect(() => {
    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [hasActiveFilters]);

  const currentPage = useMemo(() => {
    const page = Number.parseInt(searchParams.get('page') ?? '', 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const search = searchParams.get('search') ?? '';
  const orderBy = searchParams.get('orderBy') ?? '';
  const orderDirection = (searchParams.get('orderDirection') ?? 'asc') as 'asc' | 'desc';

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(prev => {
        prev.set('search', value);
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleSort = useCallback(
    (columnName: string) => {
      setSearchParams(prev => {
        const currentOrderBy = prev.get('orderBy');
        const currentOrderDirection = prev.get('orderDirection') ?? 'asc';

        if (currentOrderBy === columnName) {
          // Three-state cycle: asc -> desc -> no sort -> asc
          if (currentOrderDirection === 'asc') {
            prev.set('orderDirection', 'desc');
          } else if (currentOrderDirection === 'desc') {
            // Remove sorting (reset to default)
            prev.delete('orderBy');
            prev.delete('orderDirection');
          }
        } else {
          // Set new column and default to ascending
          prev.set('orderBy', columnName);
          prev.set('orderDirection', 'asc');
        }
        prev.set('page', '1'); // Reset to first page when sorting
        return prev;
      });
    },
    [setSearchParams],
  );

  const [inputValue, setInputValue] = useState(search);

  // Update input value when search prop changes (e.g., from URL)
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(inputValue);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearch(inputValue);
    }
  };

  const handleSearchClear = () => {
    setInputValue('');
    setSearch('');
  };

  const policyStatuses = usePolicyStatuses();

  // Combine policy status options from selected products
  const statusOptions = useMemo(() => {
    const allStatusOptions = new Map<string, string>();

    const statusOptions = accountingDashboardConfig?.filtersConfig?.policyStatus?.options;
    if (statusOptions) {
      statusOptions.forEach(option => {
        const { label, value } =
          typeof option === 'string'
            ? {
                value: option,
                label: t([`policyStatus.${option}`, `options.${option}`, option]),
              }
            : option;

        // Use first label encountered for each value
        if (!allStatusOptions.has(value as string)) {
          allStatusOptions.set(value as string, label as string);
        }
      });
    }

    if (allStatusOptions.size > 0) {
      return Array.from(allStatusOptions.entries()).map(([value, label]) => ({
        value,
        label,
      }));
    }

    // Fallback: derive from policyStatuses and map to backend enums.
    // CONTRACT_SIGNED passes through: the backend expands it to CONFIRMED + signedAt set.
    const statusMapping: Record<string, string> = {
      ACTIVE_POLICY: 'POLICY_STARTED',
      STOPPED_POLICY: 'POLICY_STOPPED',
      SUSPENDED_POLICY: 'POLICY_SUSPENDED',
    };

    return Object.keys(policyStatuses)
      .filter(key => keepSignedStatuses(key))
      .map(key => ({
        value: statusMapping[key] ?? key,
        label: t(`policyStatus.${statusMapping[key] ?? key}`),
      }));
  }, [accountingDashboardConfig, policyStatuses]);

  const terminationReasonConfigMeta = accountingDashboardConfig.filtersConfig.terminationReason;

  const terminationReasonOptions = useMemo(
    () =>
      groupTerminationReasonOptions(
        accountingDashboardConfig?.filtersConfig?.terminationReason?.options ?? [],
        reason => t([`stopReasons.${reason}`, `options.${reason}`, reason]),
      ),
    [accountingDashboardConfig, t],
  );

  // Derive statusIds from URL params
  const statusIds = useMemo(() => {
    const urlStatusIds = searchParams.getAll('statusIds');
    if (urlStatusIds.length === 0 || statusOptions.length === 0) return [];
    return urlStatusIds
      .map(id => statusOptions.find(opt => opt.value === id))
      .filter((opt): opt is { value: string; label: string } => opt !== undefined);
  }, [searchParams, statusOptions]);

  // Derive terminationReasonIds from URL params
  const terminationReasonIds = useMemo(() => {
    const urlTerminationReasonIds = searchParams.getAll('terminationReasonIds');
    if (urlTerminationReasonIds.length === 0 || terminationReasonOptions.length === 0) return [];
    return terminationReasonOptions.filter(({ values }) =>
      values.some(value => urlTerminationReasonIds.includes(value)),
    );
  }, [searchParams, terminationReasonOptions]);

  // Handler to update productIds in URL
  const setProductIds = useCallback(
    (newProductIds: Array<{ value: string; label: string }>) => {
      setSearchParams(prev => {
        prev.delete('productIds');
        newProductIds.forEach(id => {
          prev.append('productIds', id.value);
        });
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  // Handler to update statusIds in URL
  const setStatusIds = useCallback(
    (newStatusIds: Array<{ value: string; label: string }>) => {
      setSearchParams(prev => {
        prev.delete('statusIds');
        newStatusIds.forEach(id => {
          prev.append('statusIds', id.value);
        });
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  // Handler to update terminationReasonIds in URL
  const setTerminationReasonIds = useCallback(
    (newTerminationReasonIds: Array<{ value: string; label: string }>) => {
      setSearchParams(prev => {
        prev.delete('terminationReasonIds');
        newTerminationReasonIds.forEach(id => {
          prev.append('terminationReasonIds', id.value);
        });
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  // Handler to update dateFilter in URL
  const setDateFilter = useCallback(
    (
      updater: (prev: { startDate?: string; endDate?: string }) => {
        startDate?: string;
        endDate?: string;
      },
    ) => {
      setSearchParams(prev => {
        const current = {
          startDate: prev.get('startDate') || undefined,
          endDate: prev.get('endDate') || undefined,
        };
        const updated = updater(current);
        if (updated.startDate) {
          prev.set('startDate', updated.startDate);
        } else {
          prev.delete('startDate');
        }
        if (updated.endDate) {
          prev.set('endDate', updated.endDate);
        } else {
          prev.delete('endDate');
        }
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const { data: brokerageFirmId } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    select: data => data.brokerageFirmId,
  });

  const {
    data: policies,
    isLoading,
    isError,
  } = useQuery({
    ...viewSpecificControllerGetAccountingSummaryForBrokerageFirmQueryOptions(brokerageFirmId!, {
      page: currentPage,
      unpaidInvoiceIssuedAfter: toInvoiceDateBound(dateFilter.startDate, 'start'),
      unpaidInvoiceIssuedBefore: toInvoiceDateBound(dateFilter.endDate, 'end'),
      productIds: productIds.length > 0 ? productIds.map(v => v.value) : undefined,
      statuses:
        statusIds.length > 0
          ? statusIds.map(
              v =>
                v.value as ViewSpecificControllerGetAccountingSummaryForBrokerageFirmQueryParamsStatusesEnumKey,
            )
          : undefined,
      terminationReasons:
        terminationReasonIds.length > 0
          ? terminationReasonIds.flatMap(({ values }) => values)
          : undefined,
      queryString: search || undefined,
      hasFormalNotice,
      hasAutomaticRetry,
      unpaidDurationDays,
      order:
        orderBy && orderDirection
          ? {
              by: orderBy === 'customerName' ? 'customerName' : orderBy,
              direction: orderDirection,
            }
          : undefined,
    }),
    select: response => ({
      ...response,
      data: response.data?.map(element => ({
        ...element,
        productId: element.policy.productId,
        policyId: element.policy.id,
        status: element.policy.status,
        createdAt: new Date(element.policy.createdAt),
        oldestUnpaidInvoiceIssuedAt: new Date(element.oldestUnpaidInvoiceIssuedAt),
        lastFormalNoticeSentAt: element.lastFormalNoticeSentAt
          ? new Date(element.lastFormalNoticeSentAt)
          : null,
        customFields: keyValueFieldsToObject<{ customStatus: string }>(
          element.policy.customFields ?? [],
        ),
        customer: element.customer,
      })),
    }),
    enabled: Boolean(brokerageFirmId),
  });

  const isMultiProduct = productOptions.length > 1;
  const displayFilters = hasActiveFilters;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('accountingDashboard.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearchSubmit} className="mb-4">
          <Group align="stretch" gap={2} className="mb-4">
            <div className="w-full">
              <Input
                icon={<SearchIcon />}
                placeholder={t('accountingDashboard.search.placeholder')}
                value={inputValue}
                onChange={e => {
                  const newValue = e.target.value;
                  setInputValue(newValue);
                  if (newValue.length === 0) {
                    handleSearchClear();
                  }
                }}
                onKeyDown={handleSearchKeyDown}
                onClear={handleSearchClear}
                clearable
                aria-describedby="search-help-text"
              />
              <Text size="xs" tone="muted" id="search-help-text" className="mt-1">
                {t('accountingDashboard.search.helpText')}
              </Text>
            </div>
            <Button type="submit" className="mb-0">
              {t('accountingDashboard.search.submit')}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  size="icon"
                  data-testid="filters-button"
                  className="mb-0"
                >
                  <div className="relative">
                    {displayFilters && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-secondary rounded-full" />
                    )}
                    <ListFilter />
                  </div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {showFilters
                    ? t('accountingDashboard.filters.toggle.hide')
                    : t('accountingDashboard.filters.toggle.show')}
                </p>
              </TooltipContent>
            </Tooltip>
          </Group>
        </form>
        {showFilters &&
          (() => {
            // Compute all available filter options once
            const allFilterOptions: Array<{ value: string; label: string }> = [
              { value: 'status', label: t('accountingDashboard.filters.fields.status') },
              {
                value: 'invoiceDate',
                label: t('accountingDashboard.filters.fields.invoiceDate'),
              },
            ];
            if (isMultiProduct) {
              allFilterOptions.push({
                value: 'product',
                label: t('accountingDashboard.filters.fields.product'),
              });
            }
            // Only include terminationReason if conditions are met
            if (terminationReasonOptions.length > 0) {
              allFilterOptions.push({
                value: 'terminationReason',
                label:
                  terminationReasonConfigMeta?.displayName ??
                  t('accountingDashboard.filters.fields.terminationReason'),
              });
            }
            allFilterOptions.push({
              value: 'formalNotice',
              label: t('accountingDashboard.filters.fields.formalNotice'),
            });
            if (automaticRetryEnabled) {
              allFilterOptions.push({
                value: 'automaticRetry',
                label: t('accountingDashboard.filters.fields.automaticRetry'),
              });
            }
            allFilterOptions.push({
              value: 'unpaidDuration',
              label: t('accountingDashboard.filters.fields.unpaidDuration'),
            });

            return (
              <div className="p-4 bg-muted/50 border rounded-lg mb-4">
                <Stack gap={2}>
                  {filterFields.map((field, index) => {
                    // Get available filter options for this row (current field + unused fields)
                    const usedFields = new Set(filterFields);
                    const availableFilterOptions = allFilterOptions.filter(
                      opt => opt.value === field || !usedFields.has(opt.value),
                    );

                    const handleFieldChange = (newField: string) => {
                      // Clear values for the old field
                      if (field === 'status') setStatusIds([]);
                      if (field === 'invoiceDate')
                        setDateFilter(() => ({
                          startDate: undefined,
                          endDate: undefined,
                        }));
                      if (field === 'product') setProductIds([]);
                      if (field === 'terminationReason') setTerminationReasonIds([]);
                      if (field === 'formalNotice') {
                        setSearchParams(prev => {
                          prev.delete('hasFormalNotice');
                          prev.set('page', '1');
                          return prev;
                        });
                      }
                      if (field === 'automaticRetry') {
                        setSearchParams(prev => {
                          prev.delete('hasAutomaticRetry');
                          prev.set('page', '1');
                          return prev;
                        });
                      }
                      if (field === 'unpaidDuration') {
                        setSearchParams(prev => {
                          prev.delete('unpaidDurationDays');
                          prev.set('page', '1');
                          return prev;
                        });
                      }

                      // Update the field at this index
                      setFilterFields(prev => {
                        const next = [...prev];
                        next[index] = newField;
                        return next;
                      });
                    };

                    const handleRemove = () => {
                      // Clear values for this field
                      if (field === 'status') setStatusIds([]);
                      if (field === 'invoiceDate')
                        setDateFilter(() => ({
                          startDate: undefined,
                          endDate: undefined,
                        }));
                      if (field === 'product') setProductIds([]);
                      if (field === 'terminationReason') setTerminationReasonIds([]);
                      if (field === 'formalNotice') {
                        setSearchParams(prev => {
                          prev.delete('hasFormalNotice');
                          prev.set('page', '1');
                          return prev;
                        });
                      }
                      if (field === 'automaticRetry') {
                        setSearchParams(prev => {
                          prev.delete('hasAutomaticRetry');
                          prev.set('page', '1');
                          return prev;
                        });
                      }
                      if (field === 'unpaidDuration') {
                        setSearchParams(prev => {
                          prev.delete('unpaidDurationDays');
                          prev.set('page', '1');
                          return prev;
                        });
                      }

                      // Remove this filter, but keep at least one
                      if (filterFields.length > 1) {
                        setFilterFields(prev => prev.filter((_, i) => i !== index));
                      }
                    };

                    const getFieldLabel = (f: string) => {
                      if (f === 'status') return t('accountingDashboard.filters.fields.status');
                      if (f === 'invoiceDate')
                        return t('accountingDashboard.filters.fields.invoiceDate');
                      if (f === 'product') return t('accountingDashboard.filters.fields.product');
                      if (f === 'terminationReason')
                        return (
                          terminationReasonConfigMeta?.displayName ??
                          t('accountingDashboard.filters.fields.terminationReason')
                        );
                      if (f === 'formalNotice')
                        return t('accountingDashboard.filters.fields.formalNotice');
                      if (f === 'automaticRetry')
                        return t('accountingDashboard.filters.fields.automaticRetry');
                      if (f === 'unpaidDuration')
                        return t('accountingDashboard.filters.fields.unpaidDuration');
                      return f;
                    };

                    return (
                      <Group key={`${field}-${index}`} gap={2} className="group">
                        <Select value={field} onValueChange={handleFieldChange}>
                          <SelectTrigger className="w-72">
                            <SelectValue>{getFieldLabel(field)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {availableFilterOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {field === 'status' && (
                          <MultiSelect
                            options={statusOptions}
                            onChange={v => setStatusIds(v.map(value => value))}
                            value={statusIds}
                            placeholder={t('accountingDashboard.filters.placeholders.statuses')}
                          />
                        )}

                        {field === 'invoiceDate' && (
                          <Group align="stretch" gap={2}>
                            <div className="w-40">
                              <DateInput
                                className="w-full"
                                placeholder={t(
                                  'accountingDashboard.filters.placeholders.startDate',
                                )}
                                value={toInvoiceDateDay(dateFilter.startDate)}
                                onChange={newDate =>
                                  setDateFilter(d => ({ ...d, startDate: newDate }))
                                }
                              />
                            </div>
                            <div className="w-40">
                              <DateInput
                                className="w-full"
                                placeholder={t('accountingDashboard.filters.placeholders.endDate')}
                                value={toInvoiceDateDay(dateFilter.endDate)}
                                onChange={newDate =>
                                  setDateFilter(d => ({ ...d, endDate: newDate }))
                                }
                              />
                            </div>
                          </Group>
                        )}

                        {field === 'product' && isMultiProduct && (
                          <MultiSelect
                            options={productOptions}
                            onChange={v => setProductIds(v.map(value => value))}
                            value={productIds}
                            placeholder={t('accountingDashboard.filters.placeholders.products')}
                          />
                        )}

                        {field === 'terminationReason' && terminationReasonOptions.length > 0 && (
                          <MultiSelect
                            options={terminationReasonOptions.map(option => ({
                              value: option.value as string,
                              label: option.label as string,
                            }))}
                            onChange={v => setTerminationReasonIds(v.map(value => value))}
                            value={terminationReasonIds}
                            placeholder={
                              terminationReasonConfigMeta?.inputPlaceholder ??
                              t('accountingDashboard.filters.placeholders.terminationReasons')
                            }
                          />
                        )}

                        {field === 'formalNotice' && (
                          <div className="w-48">
                            <Select
                              value={
                                hasFormalNotice === true
                                  ? 'with'
                                  : hasFormalNotice === false
                                    ? 'without'
                                    : 'all'
                              }
                              onValueChange={value => {
                                setSearchParams(prev => {
                                  if (value === 'with') {
                                    prev.set('hasFormalNotice', 'true');
                                  } else if (value === 'without') {
                                    prev.set('hasFormalNotice', 'false');
                                  } else {
                                    prev.delete('hasFormalNotice');
                                  }
                                  prev.set('page', '1');
                                  return prev;
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t('accountingDashboard.filters.formalNotice.all')}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">
                                  {t('accountingDashboard.filters.formalNotice.all')}
                                </SelectItem>
                                <SelectItem value="with">
                                  {t('accountingDashboard.filters.formalNotice.with')}
                                </SelectItem>
                                <SelectItem value="without">
                                  {t('accountingDashboard.filters.formalNotice.without')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {field === 'automaticRetry' && automaticRetryEnabled && (
                          <div className="w-48">
                            <Select
                              value={
                                hasAutomaticRetry === true
                                  ? 'with'
                                  : hasAutomaticRetry === false
                                    ? 'without'
                                    : 'all'
                              }
                              onValueChange={value => {
                                setSearchParams(prev => {
                                  if (value === 'with') {
                                    prev.set('hasAutomaticRetry', 'true');
                                  } else if (value === 'without') {
                                    prev.set('hasAutomaticRetry', 'false');
                                  } else {
                                    prev.delete('hasAutomaticRetry');
                                  }
                                  prev.set('page', '1');
                                  return prev;
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t('accountingDashboard.filters.automaticRetry.all')}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">
                                  {t('accountingDashboard.filters.automaticRetry.all')}
                                </SelectItem>
                                <SelectItem value="with">
                                  {t('accountingDashboard.filters.automaticRetry.with')}
                                </SelectItem>
                                <SelectItem value="without">
                                  {t('accountingDashboard.filters.automaticRetry.without')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {field === 'unpaidDuration' && (
                          <Group gap={2}>
                            <Text inline size="sm" weight="medium">
                              &ge;
                            </Text>
                            <Input
                              type="number"
                              placeholder={t(
                                'accountingDashboard.filters.placeholders.unpaidDurationDays',
                              )}
                              value={unpaidDurationDays?.toString() || ''}
                              onChange={e => {
                                const value = e.target.value;
                                setSearchParams(prev => {
                                  if (value === '' || Number.isNaN(Number.parseInt(value, 10))) {
                                    prev.delete('unpaidDurationDays');
                                  } else {
                                    prev.set('unpaidDurationDays', value);
                                  }
                                  prev.set('page', '1');
                                  return prev;
                                });
                              }}
                              min="0"
                              step="1"
                              className="w-32"
                            />
                          </Group>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-[opacity] duration-200 ease-in-out group-hover:duration-0"
                          onClick={handleRemove}
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </Group>
                    );
                  })}

                  {(() => {
                    // Determine available filters that can be added
                    const usedFields = new Set(filterFields);
                    const availableFilters: string[] = [];
                    if (!usedFields.has('status')) availableFilters.push('status');
                    if (!usedFields.has('invoiceDate')) availableFilters.push('invoiceDate');
                    if (isMultiProduct && !usedFields.has('product'))
                      availableFilters.push('product');
                    if (terminationReasonOptions.length > 0 && !usedFields.has('terminationReason'))
                      availableFilters.push('terminationReason');
                    if (!usedFields.has('formalNotice')) availableFilters.push('formalNotice');
                    if (!usedFields.has('unpaidDuration')) availableFilters.push('unpaidDuration');

                    return availableFilters.length > 0 ? (
                      <Button
                        className="w-fit"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Add the first available filter
                          setFilterFields(prev => [...prev, availableFilters[0]!]);
                        }}
                      >
                        +{' '}
                        <span className="underline ml-1">
                          {t('accountingDashboard.filters.addCondition')}
                        </span>
                      </Button>
                    ) : null;
                  })()}
                </Stack>
              </div>
            );
          })()}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort('customerName')}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                  title={`Click to sort ${orderBy === 'customerName' ? (orderDirection === 'asc' ? 'descending' : 'remove sort') : 'ascending'}`}
                >
                  {t('accountingDashboard.columns.customer')}
                  {orderBy === 'customerName' ? (
                    orderDirection === 'asc' ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )
                  ) : (
                    <ArrowUpDown className="h-4 w-4 opacity-50" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t('accountingDashboard.columns.status')}</TableHead>
              <TableHead>{t('accountingDashboard.columns.oldestUnpaidInvoiceDate')}</TableHead>
              <TableHead>{t('accountingDashboard.columns.numberOfUnpaidInvoices')}</TableHead>
              <TableHead>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <span>{t('accountingDashboard.columns.unpaidDuration')}</span>
                  <span>{t('accountingDashboard.columns.unpaidDurationUnit')}</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <LucideInfo className="w-4 h-4 stroke-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="text-muted-foreground">
                      {t('accountingDashboard.columns.unpaidDurationTooltip')}
                    </TooltipContent>
                  </Tooltip>
                </span>
              </TableHead>
              <TableHead>{t('accountingDashboard.columns.lastFormalNoticeSent')}</TableHead>
              {automaticRetryEnabled && (
                <TableHead>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span>{t('accountingDashboard.columns.nextRecoveryAction')}</span>
                    <Tooltip>
                      <TooltipTrigger
                        aria-label={t('accountingDashboard.columns.nextRecoveryActionTooltip')}
                      >
                        <LucideInfo
                          aria-hidden="true"
                          className="w-4 h-4 stroke-muted-foreground"
                        />
                      </TooltipTrigger>
                      <TooltipContent className="text-muted-foreground">
                        {t('accountingDashboard.columns.nextRecoveryActionTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
              )}
              {isMultiProduct && <TableHead>{t('accountingDashboard.columns.product')}</TableHead>}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(policies?.data?.length === 0 || !policies?.data) && (
              <NoResultsRow
                isSearchPoliciesLoading={isLoading}
                isSearchPoliciesError={isError}
                pageSize={PAGE_SIZE}
              />
            )}
            {policies?.data?.map(policy => (
              <TableRow key={policy.policyId}>
                <TableCell>
                  {customerDisplayName(
                    [policy.customer],
                    productsConfig[policy.productId]?.customerInformations?.fieldsConfig,
                  ) ?? policy.customer.id}
                </TableCell>
                <TableCell>
                  <PolicyStatus policy={policy} />
                </TableCell>
                <TableCell>{formatDate(policy.oldestUnpaidInvoiceIssuedAt)}</TableCell>
                <TableCell>{policy.numberOfUnpaidInvoices}</TableCell>
                <TableCell>{Math.trunc(policy.unpaidDurationInDay)}</TableCell>
                <TableCell>
                  {policy.lastFormalNoticeSentAt ? (
                    <Tooltip>
                      <TooltipTrigger>
                        {dayjs().locale(i18n.language).to(dayjs(policy.lastFormalNoticeSentAt))}
                      </TooltipTrigger>
                      <TooltipContent>{formatDate(policy.lastFormalNoticeSentAt)}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <div className="italic text-muted-foreground">
                      {t('accountingDashboard.lastFormalNotice.none')}
                    </div>
                  )}
                </TableCell>
                {automaticRetryEnabled && (
                  <TableCell>
                    <div className="min-w-48">
                      {policy.plannedRecoveryAction ? (
                        <div className="text-sm">
                          <div>
                            {t(
                              `accountingDashboard.recovery.actions.${policy.plannedRecoveryAction.name}`,
                            )}
                          </div>
                          <time
                            dateTime={policy.plannedRecoveryAction.scheduledAt}
                            className="text-muted-foreground"
                          >
                            {formatDate(
                              new Date(policy.plannedRecoveryAction.scheduledAt),
                              i18n.language,
                            )}
                          </time>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {t('accountingDashboard.recovery.noScheduledAction')}
                        </div>
                      )}
                    </div>
                  </TableCell>
                )}
                {isMultiProduct && <TableCell>{t(`products.${policy.productId}.label`)}</TableCell>}
                <TableCell>
                  <Link to={`/policies/${policy.policyId}/invoices`}>
                    <Button variant="ghost" size="sm">
                      {t('accountingDashboard.actions.view')}
                      <EyeIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <CardContent>
        <PaginationComponent
          totalData={policies?.totalCount ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
}
